import type { UserDetail } from '@shared/types';
import type { PersonnelDetail } from '@/types/personnelDetail';
import { formatDateDisplay } from '@/utils/dateHelpers';

const VIETNAM_LOCALE = 'vi-VN' as const;

// Fix trường hợp chuỗi UTF-8 bị đọc sai encoding (ví dụ: 'SÆ¡ yáº¿u lÃ½ lá»ch')
const needsEncodingFix = (value: string): boolean => {
  // Các pattern mojibake hay gặp với tiếng Việt
  return /Ã|Â|Æ|Ð|áº|á»|â|Â·/.test(value);
};

const fixUtf8Mojibake = (value?: string | null): string => {
  if (!value) return '';
  if (!needsEncodingFix(value)) return value;
  try {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore escape vẫn tồn tại trong browser, dùng tạm để sửa encoding
    return decodeURIComponent(escape(value));
  } catch {
    return value;
  }
};

const normalizeCode = (value?: string) => (value ?? '').trim().toLowerCase();

const mapTrainingFormToVi = (value?: string): string => {
  const code = normalizeCode(value);
  const mapping: Record<string, string> = {
    official: 'Chính quy',
    'full-time': 'Chính quy',
    parttime: 'Không chính quy',
    'part-time': 'Không chính quy',
    inservice: 'Vừa làm vừa học',
    'in-service': 'Vừa làm vừa học',
    'work-study': 'Vừa làm vừa học',
    'distance-learning': 'Đào tạo từ xa',
    online: 'Đào tạo từ xa',
    bridging: 'Liên thông',
    vocational: 'Đào tạo nghề',
    international: 'Đào tạo quốc tế',
  };
  return mapping[code] ?? value ?? '';
};

const mapEducationLevelToVi = (value?: string): string => {
  const code = normalizeCode(value);
  const mapping: Record<string, string> = {
    highschool: 'Trung học',
    'high-school': 'Trung học',
    intermediate: 'Trung cấp',
    college: 'Cao đẳng',
    university: 'Đại học',
    postgraduate: 'Sau đại học',
    master: 'Sau đại học',
    doctor: 'Sau đại học',
  };
  return mapping[code] ?? value ?? '';
};

const mapRelationshipToVi = (value?: string): string => {
  const code = normalizeCode(value);
  const mapping: Record<string, string> = {
    parent: 'Cha/Mẹ',
    father: 'Cha',
    mother: 'Mẹ',
    spouse: 'Vợ/Chồng',
    husband: 'Chồng',
    wife: 'Vợ',
    child: 'Con',
    son: 'Con',
    daughter: 'Con',
    sibling: 'Anh/Chị/Em',
    brother: 'Anh/Em',
    sister: 'Chị/Em',
    grandparent: 'Ông/Bà',
    grandpa: 'Ông',
    grandma: 'Bà',
    grandchild: 'Cháu',
    other: 'Khác',
  };
  return mapping[code] ?? value ?? '';
};

export interface AdditionalData {
  educationHistory?: Array<{
    trainingInstitution?: string;
    major?: string;
    trainingForm?: string;
    educationLevel?: string;
    fromDate?: string;
    toDate?: string;
  }>;
  workExperience?: Array<{
    company?: string;
    position?: string;
    department?: string;
    salary?: number | string;
    fromDate?: string;
    toDate?: string;
  }>;
  familyMembers?: Array<{
    relationship?: string;
    fullName?: string;
    birthYear?: number | null;
    phone?: string;
    occupation?: string;
    workplace?: string;
    address?: string;
  }>;
  certificates?: Array<{
    id?: number | string;
    certificateNumber?: string;
    type?: string;
    issueDate?: string;
    expiryDate?: string;
    notes?: string;
    issuingBody?: string;
    issuingUnit?: string;
    attachments?: Array<{ id: number; fileName: string; fileUrl: string }>;
  }>;
  partyHistory?: Array<{
    cardNumber?: string;
    form?: string;
    date?: string;
    fromDate?: string;
    toDate?: string;
    admissionPlace?: string;
    transferPlace?: string;
  }>;
  personalFiles?: Array<{
    fileName?: string;
    fileUrl?: string;
  }>;
  idCardFront?: string;
  idCardBack?: string;
  passportCover?: string;
  passportPhoto?: string;
  horoscope?: string;
}

/**
 * Format date range
 */
const formatDateRange = (fromDate?: string, toDate?: string): string | undefined => {
  if (!fromDate && !toDate) return undefined;
  const from = fromDate ? formatDateDisplay(fromDate) : '';
  const to = toDate ? formatDateDisplay(toDate) : '';
  if (from && to) return `${from}-${to}`;
  if (from) return `Từ ${from}`;
  if (to) return `Đến ${to}`;
  return undefined;
};

/**
 * Format salary to Vietnamese currency
 */
const formatSalary = (salary?: number | string): string => {
  if (!salary) return '';
  const numSalary = typeof salary === 'string' ? Number(salary) : salary;
  return `${numSalary.toLocaleString(VIETNAM_LOCALE)} VND`;
};

/**
 * Map UserDetail từ API sang PersonnelDetail format cho frontend
 */
export const mapUserDetailToPersonnelDetail = (
  user: UserDetail,
  additionalData?: AdditionalData
): PersonnelDetail => {
  const trainingPlaces =
    additionalData?.educationHistory?.map((edu) => ({
      place: edu.trainingInstitution ?? '',
      major: edu.major ?? '',
      type: mapTrainingFormToVi(edu.trainingForm),
      degree: mapEducationLevelToVi(edu.educationLevel),
      time: formatDateRange(edu.fromDate, edu.toDate) ?? '',
    })) ?? [];

  const workExperience =
    additionalData?.workExperience?.map((exp) => ({
      company: exp.company ?? '',
      role: exp.position ?? '',
      department: exp.department ?? '',
      salary: formatSalary(exp.salary),
      time: formatDateRange(exp.fromDate, exp.toDate) ?? '',
    })) ?? [];

  const familyMembers =
    additionalData?.familyMembers?.map((member) => ({
      relationship: mapRelationshipToVi(member.relationship),
      name: member.fullName ?? '',
      birthYear: member.birthYear ?? null,
      phone: member.phone ?? '',
      occupation: member.occupation ?? '',
      workplace: member.workplace ?? '',
      address: member.address ?? '',
    })) ?? [];

  const certificates =
    additionalData?.certificates?.map((cert) => ({
      id: cert.certificateNumber || cert.id?.toString() || '',
      issuingBody: (cert as any).issuingBody ?? (cert as any).issuingUnit ?? '',
      type: cert.type ?? '',
      time: formatDateRange(cert.issueDate, cert.expiryDate) ?? '',
      notes: cert.notes ?? '',
      attachments: cert.attachments ?? [],
    })) ?? [];

  const partyHistory =
    additionalData?.partyHistory?.map((party) => {
      // Format time: nếu có date thì dùng date, nếu không thì dùng date range
      let time = '';
      if (party.date) {
        time = formatDateDisplay(party.date);
      } else if ((party as any).fromDate || (party as any).toDate) {
        time = formatDateRange((party as any).fromDate, (party as any).toDate) ?? '';
      }
      return {
        cardNumber: party.cardNumber ?? '',
        form: party.form ?? '',
        time,
        admissionPlace: party.admissionPlace ?? '',
        transferPlace: party.transferPlace ?? '',
      };
    }) ?? [];

  const storedFiles =
    additionalData?.personalFiles?.map((file) => ({
      name: fixUtf8Mojibake(file.fileName),
      url: file.fileUrl ?? '',
    })) ?? [];

  const status: PersonnelDetail['status'] = user.isActive && !user.isBlocked ? 'active' : 'locked';
  const formattedStartDate = user.startDate ? formatDateDisplay(user.startDate) : '';
  const username = user.email?.split('@')[0] ?? '';

  const currentJobSalary = user.salary !== null && user.salary !== undefined
    ? `${Number(user.salary).toLocaleString(VIETNAM_LOCALE)} đ`
    : user.monthlySalary
    ? `${Number(user.monthlySalary).toLocaleString(VIETNAM_LOCALE)} đ`
    : user.hourlySalary
    ? `${Number(user.hourlySalary).toLocaleString(VIETNAM_LOCALE)} đ/giờ`
    : '';

  let mappedSkills: string[] = [];
  const rawSkills = (user as any).skills;
  if (Array.isArray(rawSkills)) {
    mappedSkills = rawSkills.map((s: any) => String(s).trim()).filter(Boolean);
  } else if (typeof rawSkills === 'string' && rawSkills.trim()) {
    try {
      const parsed = JSON.parse(rawSkills);
      if (Array.isArray(parsed)) {
        mappedSkills = parsed.map((s: any) => String(s).trim()).filter(Boolean);
      } else {
        mappedSkills = rawSkills.split(',').map((s) => s.trim()).filter(Boolean);
      }
    } catch {
      mappedSkills = rawSkills.split(',').map((s) => s.trim()).filter(Boolean);
    }
  }

  return {
    id: user.id.toString(),
    employeeCode: user.employeeCode ?? '',
    fullName: user.fullName,
    position: user.position ?? '',
    company: user.company ?? '',
    jobTitle: user.position ?? '',
    status,
    startDate: formattedStartDate,
    avatar: user.avatar,
    role: user.role,
    email: user.email,
    phone: user.phone,
    dateOfBirth: user.dateOfBirth ? formatDateDisplay(user.dateOfBirth) : undefined,
    hometown: user.hometown,
    gender: user.gender,
    ethnicity: user.ethnicity,
    religion: user.religion,
    placeOfBirth: user.placeOfBirth,
    address: user.currentAddress,
    permanentAddress: user.permanentAddress,
    nationality: user.nationality,
    taxCode: user.personalTaxCode ?? '',
    username,
    description: user.selfDescription ?? '',
    bankAccount: user.bankAccountNumber ?? '',
    bankAccountHolder: user.bankAccountName ?? '',
    bankName: user.bankName ?? '',
    bankBranch: user.bankBranch ?? '',
    personalEmail: user.personalEmail ?? '',
    maritalStatus: user.maritalStatus ?? '',
    militaryService: user.militaryService ?? '',
    permanentWard: user.permanentWard ?? '',
    currentWard: user.currentWard ?? '',
    idNumber: user.idNumber ?? '',
    idIssuePlace: user.idIssuePlace ?? '',
    idIssueDate: user.idIssueDate ? formatDateDisplay(user.idIssueDate) : '',
    idExpiryDate: user.idExpiryDate ? formatDateDisplay(user.idExpiryDate) : '',
    passportType: user.passportType ?? '',
    passportNumber: user.passportNumber ?? '',
    passportIssuePlace: user.passportIssuePlace ?? '',
    passportIssueDate: user.passportIssueDate ? formatDateDisplay(user.passportIssueDate) : '',
    passportExpiryDate: user.passportExpiryDate ? formatDateDisplay(user.passportExpiryDate) : '',
    officialContractDate: user.officialContractDate ? formatDateDisplay(user.officialContractDate) : '',
    directManager: user.directManager ?? '',
    laborType: user.laborType ?? '',
    referencePerson: user.referencePerson ?? '',
    currentJob: {
      company: user.company ?? '',
      department: '',
      position: user.position ?? '',
      role: user.jobLocation || user.position || '',
      salary: currentJobSalary,
      startDate: formattedStartDate,
    },
    education: {
      highestLevel: user.generalEducationLevel ?? '',
      generalLevel: user.generalEducationLevel ?? '',
      major: user.specialization ?? '',
      yearsOfExperience: user.yearsOfExperience ?? 0,
    },
    trainingPlaces,
    workExperience,
    familyMembers,
    certificates,
    skills: mappedSkills,
    partyHistory,
    idCardFront: additionalData?.idCardFront,
    idCardBack: additionalData?.idCardBack,
    passportCover: additionalData?.passportCover,
    passportPhoto: additionalData?.passportPhoto,
    horoscope: additionalData?.horoscope,
    storedFiles,
  };
};
